import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { debounce } from 'lodash';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

import Chip from 'components/Chip';
import CustomButton from 'components/CustomButton';
import Pagination from 'components/Pagination';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import { useNavigateWithClient } from 'hooks';
import { CustomTooltip } from 'components/InfoTooltip';
import { SearchTextField } from 'components/CustomFields';
import { useServices, useSnackbar } from 'contexts';
import { getCaseMasterProblems } from 'services';
import { Session, snakeCaseToSentence } from 'utils';
import NoAccessTooltip, { NO_BATCH_ACCESS_MESSAGE } from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';
import { useCaseMasterAccess } from './CaseMasterAccessContext';
import CaseMasterLockMessage from './CaseMasterLockMessage';
import CaseFilters from './CaseFilters';
import { CaseAttemptFacet, firstTagName, parseCasebook, toTagLabel } from './constant';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
    padding: theme.spacing(7, 10),
  },
  header: {
    borderBottom: '1px solid #8692A3',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(6),
    paddingBottom: '22px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(6),
  },
  content: {
    marginTop: theme.spacing(7),
  },
  cards: {
    display: 'flex', flexDirection: 'column', gap: '20px',
    flexWrap: 'wrap', marginTop: '12px'
  },
  cardRoot: {
    border: '2px solid',
    width: '100%',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    // Stretched rather than centred: the right column spaces the domain chip
    // and the button to the card's own top and bottom.
    alignItems: 'stretch',
    gap: theme.spacing(6),
    padding: theme.spacing(7, 6),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary['clr-100'],
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  // The domain chip sits at the top of this column and the button at the
  // bottom, so it stretches to the card's height and pushes them apart.
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    flexShrink: 0,
    gap: theme.spacing(4),
  },
  tagRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
  },
  tagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  tagIcon: {
    fontSize: '20px',
    color: theme.palette.neutral['clr-400'],
  },
  // The one mark in the row that is not a tag, so it is the one thing here
  // carrying colour.
  casebookIcon: {
    fontSize: '20px',
    color: theme.palette.primary['clr-300'],
  },
  skeletonCardRoot: {
    width: '100%',
    border: '2px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(7, 6),
  },
  skeleton: {
    borderRadius: '4px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  viewFeedbackLink: {
    color: '#1961BF',
    cursor: 'pointer',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(6),
  },
}));

const PAGE_SIZE = 10;

// Tag references are only populated when asked for, so the list has to name
// what it intends to read. Specialization is left out: the card has no room
// for it, and every field here costs a lookup per case.
const CARD_INCLUDES = ['company', 'industry', 'jobRole', 'domain'];

// The tags listed under the title, in the order the design places them. Domain
// is deliberately absent - it rides in the chip at the top right instead.
const CardTags = [
  { key: 'company', Icon: ApartmentOutlinedIcon },
  { key: 'industry', Icon: FactoryOutlinedIcon },
  { key: 'jobRole', Icon: WorkOutlineOutlinedIcon },
];

const Card = memo(({
  _id, attempts = [], title = '', topic = '', entryPoint = null,
  feedbackAttemptId = null, tags = {}, metadata = {}
}) => {
  const classes = useStyles();
  const navigateWithClient = useNavigateWithClient();
  const removed = useSelectedBatchRemoved();

  const [startInterview, setStartInterview] = useState(false);

  const hasAttempted = attempts?.length > 0;

  // Plan-based gating via the shared access context: non-B2C → unlocked; B2C
  // free → only the flagged free case until its attempt is spent; Pro → until
  // validity; Premium → always. Batch removal takes priority over the message.
  const { getLockInfo } = useCaseMasterAccess();
  const { locked, message: lockMessage, upgradeUrl, linkText } = getLockInfo(_id);
  const disabled = removed || locked;
  const tooltipMessage = removed
    ? NO_BATCH_ACCESS_MESSAGE
    : <CaseMasterLockMessage
      message={lockMessage} upgradeUrl={upgradeUrl} linkText={linkText}
    />;
  const lockIcon = locked ? <LockOutlinedIcon sx={{ fontSize: '18px' }} /> : undefined;

  const handleOpenDialog = useCallback(() => setStartInterview(true), []);
  const handleCloseDialog = useCallback(() => setStartInterview(false), []);
  const handleViewFeedback = useCallback(() => {
    navigateWithClient(`/performance/case-master/${feedbackAttemptId}`);
  }, [navigateWithClient, feedbackAttemptId]);

  // Most cases carry no domain tag yet, but every one of them has a topic, and
  // the two say the same thing - the tags were seeded from it. Falling back
  // keeps the chip filled rather than leaving three cards in four bare.
  const domainName = toTagLabel(
    firstTagName(tags?.domain) || (topic ? snakeCaseToSentence(topic) : '')
  );

  // What separates a case written from a real casebook from one the model
  // generated: only the former names the book it came from. The ~200 generated
  // cases have nothing here, so they show no mark at all.
  const casebookLabel = parseCasebook(metadata?.casebook).label;

  // 250 of the 359 cases carry no tags at all - every one of them generated
  // rather than taken from a casebook - so for most cards this row has nothing
  // to show and is left out entirely rather than sitting empty under the title
  // holding the gap above it open.
  const rowTags = CardTags
    .map(({ key, Icon }) => ({ key, Icon, name: toTagLabel(firstTagName(tags?.[key])) }))
    .filter(({ name }) => name);

  const hasTagRow = rowTags.length > 0 || Boolean(casebookLabel);

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Chip
          content={hasAttempted ? 'Attempted' : 'Unattempted'}
          bgColor={hasAttempted ? '#DCFCE7' : '#E5F1FF'}
          color={hasAttempted ? '#009970' : '#135EBF'}
          typographyVariant='body01-bold'
          sx={{ padding: '4px 12px' }}
        />

        <Typography variant='h4-medium'>
          {title}
        </Typography>

        {
          hasTagRow &&
          <Box className={classes.tagRow}>
            {rowTags.map(({ key, Icon, name }) => (
              <Box key={key} className={classes.tagItem}>
                <Icon className={classes.tagIcon} />
                <Typography variant='body01-medium' color='neutral.clr-500'>
                  {name}
                </Typography>
              </Box>
            ))}

            {
              casebookLabel &&
              <Box className={classes.tagItem}>
                <VerifiedOutlinedIcon className={classes.casebookIcon} />
                <Typography variant='body01-semiBold' color='primary.clr-300'>
                  {casebookLabel}
                </Typography>
              </Box>
            }
          </Box>
        }
      </Box>

      <Box className={classes.cardActions}>
        {
          domainName
            ? <Chip
              content={domainName}
              bgColor='#E9EDF2'
              color='#3C434D'
              typographyVariant='body01-bold'
              sx={{ padding: '4px 12px' }}
            />
            : <Box />
        }

        {
          !hasAttempted ?
            <NoAccessTooltip show={disabled} message={tooltipMessage}>
              <CustomButton
                onClick={handleOpenDialog} variant='contained' color='primary'
                disabled={disabled} startIcon={lockIcon}
              >
                Attempt now
              </CustomButton>
            </NoAccessTooltip>
            :
            <Box className={classes.buttonGroup}>
              <NoAccessTooltip show={disabled} message={tooltipMessage}>
                <CustomButton
                  onClick={handleOpenDialog} variant='outlined' color='primary'
                  disabled={disabled} startIcon={lockIcon}
                >
                  Reattempt
                </CustomButton>
              </NoAccessTooltip>
              {
                feedbackAttemptId &&
                <Typography
                  onClick={handleViewFeedback}
                  variant='body01-link'
                  className={classes.viewFeedbackLink}
                >
                  View feedback
                </Typography>
              }
            </Box>
        }
      </Box>
      <StartAssessmentDialog
        onClose={handleCloseDialog}
        open={startInterview}
        template={{ _id: _id, name: title, type: 'case_master' }}
        codingInterview={false}
        stateParams={entryPoint}
      />
    </Box>
  );
});

const CardSkeleton = () => {
  const classes = useStyles();

  return (
    <Box className={classes.skeletonCardRoot}>
      <Box className={classes.cardContent}>
        <Skeleton variant="rounded" width={92} height={22} className={classes.skeleton} />
        <Skeleton variant="text" width={260} height={32} className={classes.skeleton} />
        <Box className={classes.tagRow}>
          {[110, 96, 104].map((width) => (
            <Skeleton
              key={width} variant="text" width={width} height={20}
              className={classes.skeleton}
            />
          ))}
        </Box>
      </Box>
      <Box className={classes.cardActions}>
        <Skeleton variant="rounded" width={80} height={22} className={classes.skeleton} />
        <Skeleton variant="rectangular" width={120} height={40} className={classes.skeleton} />
      </Box>
    </Box>
  );
};

const Challenges = ({ handleBack = () => { }, entryPoint = null }) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [progress, setProgress] = useState({ attempted: 0, total: 0 });
  // Tracked separately from the list's loading: without it the header would
  // read "Completed: 0%" until the counts land, which is a wrong number rather
  // than an absent one.
  const [progressLoading, setProgressLoading] = useState(true);

  // Page, search and facets live in one state object so that a change which
  // resets to the first page is a single update and therefore a single request.
  const [query, setQuery] = useState({ page: 0, search: '', facets: {} });
  const { page, search, facets = {} } = query;

  // Progress covers the whole catalogue, so it can't be read off the current
  // page or search result. `attemptedBy` is appended when a user attempts a
  // case, so both figures are plain counts — pageSize 1 keeps the payload small.
  useEffect(() => {
    (async () => {
      try {
        const userId = Session.userId;

        const [allCases, attemptedCases] = await Promise.all([
          service.callService(getCaseMasterProblems, {
            pageSize: 1, includeTotal: true
          }),
          userId
            ? service.callService(getCaseMasterProblems, {
              attemptedBy: userId, pageSize: 1, includeTotal: true
            })
            : null,
        ]);

        setProgress({
          total: allCases?.total || 0,
          attempted: attemptedCases?.total || 0,
        });
      } catch (error) {
        console.error('Error fetching case study progress:', error);
      } finally {
        setProgressLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // A slow request for an earlier query must not overwrite a later one's
    // results, so a superseded response is dropped.
    let current = true;

    (async () => {
      try {
        setLoading(true);

        const result = await service.callService(getCaseMasterProblems, {
          ...(search ? { title: search } : {}),
          ...Object.entries(facets).reduce((acc, [key, value]) => {
            // Every facet but the attempt status is a tag reference on the case.
            acc[key === CaseAttemptFacet.key ? key : `tags.${key}`] = value;
            return acc;
          }, {}),
          include: CARD_INCLUDES,
          pageNumber: page + 1,
          pageSize: PAGE_SIZE,
          includeTotal: true,
        });

        if (!current) return;

        const problems = result?.casemasterProblems || [];
        const currentUserId = Session.userId?.toString();

        // The Card reads its attempted state off `attempts.length`; this endpoint
        // only says who has attempted the case, so normalize to that shape.
        setChallenges(problems.map((problem) => ({
          ...problem,
          attempts: currentUserId && (problem.attemptedBy || []).includes(currentUserId)
            ? [{}]
            : [],
        })));
        setTotalCount(result?.total || 0);
      } catch (error) {
        if (!current) return;

        console.error('Error fetching case studies:', error);
        snackbar.error(error.message || "Uh Oh! Unable to get case studies. Please try again.");
      } finally {
        if (current) setLoading(false);
      }
    })();

    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, facets]);

  // Filtering happens as the user types, so there is no search button; the
  // debounce keeps it to one request per pause and page 1 of the new results.
  const handleSearch = useMemo(() => debounce((e) => {
    const value = e.target.value.trim();

    setQuery((prev) => (
      prev.search === value ? prev : { ...prev, page: 0, search: value }
    ));
  }, 500), []);

  const handlePageChange = useCallback((nextPage) => {
    setQuery((prev) => ({ ...prev, page: nextPage }));
  }, []);

  const handleFacetChange = useCallback((nextFacets) => {
    setQuery((prev) => ({ ...prev, page: 0, facets: nextFacets }));
  }, []);

  useEffect(() => () => handleSearch.cancel(), [handleSearch]);

  const completionRate = progress.total > 0
    ? (progress.attempted / progress.total) * 100
    : 0;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <ArrowBackIcon style={{ cursor: 'pointer', color: '#0062E1' }} onClick={handleBack} />
          <Typography variant='h5-medium'>Case Studies</Typography>
        </Box>

        <Box className={classes.headerActions}>
          <SearchTextField
            placeholder='Search case studies...'
            style={{ width: '320px' }}
            defaultValue={search}
            onChange={handleSearch}
          />
          {
            progressLoading
              ? <Skeleton
                variant='text' width={120} height={24}
                className={classes.skeleton}
              />
              : <CustomTooltip
                component='It represents your progress for case studies. It is based on your attempted case studies'
              >
                <Typography
                  variant='h6-medium'
                  color='primary.clr-300'
                  style={{ borderBottom: '2px dotted #135EBF', whiteSpace: 'nowrap' }}
                >
                  Completed: {completionRate.toFixed(0)}%
                </Typography>
              </CustomTooltip>
          }
        </Box>
      </Box>

      <CaseFilters onChange={handleFacetChange} />

      <Box className={classes.content}>
        {loading ? (
          <Box className={classes.cards}>
            {[...Array(5)].map((_, index) => (
              <CardSkeleton key={`card-skeleton-${index}`} />
            ))}
          </Box>
        ) : (
          <>
            {challenges.length > 0 ? (
              <Box className={classes.cards}>
                {challenges.map((data, i) => (
                  <Card
                    key={data._id || i}
                    {...data}
                    attempts={data.attempts || []}
                    entryPoint={entryPoint}
                  />
                ))}
              </Box>
            ) : (
              <Box mt={4} textAlign="center">
                <Typography variant='body01' color='neutral.clr-600'>
                  {
                    search || Object.keys(facets).length
                      ? 'No case studies match the selected filters.'
                      : 'No case studies available yet.'
                  }
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {totalCount > PAGE_SIZE && (
        <Box className={classes.footer}>
          <Typography variant='body01-bold' color='neutral.clr-900'>
            Cases: {(page * PAGE_SIZE) + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </Typography>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE)}
            page={page}
            setPage={handlePageChange}
          />
        </Box>
      )}
    </Box>
  )
}

export default Challenges;
